/**
 * 용어집 관련 Mock Store
 * OpenMetadata 용어집 API를 목업으로 처리합니다.
 */

export type GlossaryCategory = {
  id: string;
  name: string;
  displayName: string;
  fullyQualifiedName: string;
  description: string;
  version: number;
  updatedAt: number;
  updatedBy: string;
  href: string;
  deleted: boolean;
  termCount: number;
  owners?: Array<{
    id: string;
    type: string;
    name: string;
    displayName?: string;
    fullyQualifiedName?: string;
  }>;
  domains?: Array<{
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    displayName: string;
  }>;
};

export type GlossaryTerm = {
  id: string;
  name: string;
  displayName: string;
  fullyQualifiedName: string;
  description: string;
  version: number;
  updatedAt: number;
  updatedBy: string;
  href: string;
  deleted: boolean;
  status: string;
  provider: string;
  mutuallyExclusive: boolean;
  glossary?: {
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    displayName: string;
    deleted: boolean;
  };
  parent?: {
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    displayName: string;
    deleted: boolean;
  };
  relatedTerms?: Array<{
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    displayName: string;
    deleted: boolean;
  }>;
  synonyms?: string[];
  children?: Array<{
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    displayName: string;
    deleted: boolean;
  }>;
  childrenCount?: number;
  tags?: Array<{
    tagFQN: string;
    name: string;
    displayName?: string;
    description: string;
    source: string;
    labelType: string;
    state: string;
  }>;
  owners?: Array<{
    id: string;
    type: string;
    name: string;
    displayName?: string;
    fullyQualifiedName?: string;
    deleted: boolean;
  }>;
  domains?: Array<{
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    displayName: string;
    inherited?: boolean;
  }>;
  reviewers?: Array<{
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    displayName: string;
    deleted: boolean;
  }>;
  references?: Array<{
    name: string;
    endpoint: string;
  }>;
  usageCount?: number;
};

/**
 * 제조 용어집 카테고리
 */
const MANUFACTURING_GLOSSARY_CATEGORY: GlossaryCategory = {
  id: 'manufacturing-glossary',
  name: 'Manufacturing',
  displayName: '제조 용어집',
  fullyQualifiedName: 'Manufacturing',
  description: '<p>제조 프로세스 및 설비와 관련된 용어를 정리한 용어집입니다.</p>',
  version: 0.1,
  updatedAt: 1761833744665,
  updatedBy: 'admin',
  href: 'http://localhost:8585/v1/glossaries/manufacturing-glossary',
  deleted: false,
  termCount: 16,
  owners: [],
  domains: [],
};

/**
 * 품질 용어집 카테고리
 */
const QUALITY_GLOSSARY_CATEGORY: GlossaryCategory = {
  id: 'quality-glossary',
  name: 'Quality',
  displayName: '품질 관리 용어집',
  fullyQualifiedName: 'Quality',
  description: '<p>품질 관리, 검사, 시험과 관련된 용어를 정리한 용어집입니다.</p>',
  version: 0.1,
  updatedAt: 1761833744665,
  updatedBy: 'admin',
  href: 'http://localhost:8585/v1/glossaries/quality-glossary',
  deleted: false,
  termCount: 12,
  owners: [],
  domains: [],
};

/**
 * 데이터 관리 용어집 카테고리
 */
const DATA_MANAGEMENT_GLOSSARY_CATEGORY: GlossaryCategory = {
  id: 'data-management-glossary',
  name: 'DataManagement',
  displayName: '데이터 관리 용어집',
  fullyQualifiedName: 'DataManagement',
  description: '<p>데이터 관리, 데이터 품질, 데이터 거버넌스 관련 용어를 정리한 용어집입니다.</p>',
  version: 0.1,
  updatedAt: 1761833744665,
  updatedBy: 'admin',
  href: 'http://localhost:8585/v1/glossaries/data-management-glossary',
  deleted: false,
  termCount: 11,
  owners: [],
  domains: [],
};

/**
 * 용어집 카테고리 목록
 */
export const GLOSSARY_CATEGORIES = [
  MANUFACTURING_GLOSSARY_CATEGORY,
  QUALITY_GLOSSARY_CATEGORY,
  DATA_MANAGEMENT_GLOSSARY_CATEGORY,
];

/**
 * 제조 관련 용어들
 */
const MANUFACTURING_TERMS: GlossaryTerm[] = [
  {
    id: 'term-cnc',
    name: 'CNC',
    displayName: 'CNC (Computer Numerical Control)',
    fullyQualifiedName: 'Manufacturing.CNC',
    description: '<p>컴퓨터 수치 제어 시스템을 이용하여 공작 기계를 자동으로 제어하는 기술</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-cnc',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'manufacturing-glossary',
      type: 'glossary',
      name: 'Manufacturing',
      fullyQualifiedName: 'Manufacturing',
      displayName: '제조 용어집',
      deleted: false,
    },
    synonyms: ['수치제어', 'Numerical Control'],
    childrenCount: 0,
    usageCount: 10,
  },
  {
    id: 'term-turning',
    name: 'Turning',
    displayName: '터닝 (선삭)',
    fullyQualifiedName: 'Manufacturing.Turning',
    description: '<p>공작물을 회전시켜 바이트로 절삭하는 가공 방법. 원통형 부품을 제작하는 데 사용됩니다.</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-turning',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'manufacturing-glossary',
      type: 'glossary',
      name: 'Manufacturing',
      fullyQualifiedName: 'Manufacturing',
      displayName: '제조 용어집',
      deleted: false,
    },
    synonyms: ['선삭', 'Lathe Machining'],
    childrenCount: 0,
  },
  {
    id: 'term-milling',
    name: 'Milling',
    displayName: '밀링 (프레이즈)',
    fullyQualifiedName: 'Manufacturing.Milling',
    description:
      '<p>회전하는 커터(밀)를 사용하여 공작물을 절삭하는 가공 방법. 평면, 홈, 복잡한 형상 가공에 사용됩니다.</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-milling',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'manufacturing-glossary',
      type: 'glossary',
      name: 'Manufacturing',
      fullyQualifiedName: 'Manufacturing',
      displayName: '제조 용어집',
      deleted: false,
    },
    synonyms: ['프레이즈', 'Milling Machining'],
    childrenCount: 0,
  },
  {
    id: 'term-spindle-speed',
    name: 'SpindleSpeed',
    displayName: '주축 회전 속도',
    fullyQualifiedName: 'Manufacturing.SpindleSpeed',
    description: '<p>공작 기계의 주축이 분당 회전하는 횟수를 나타내는 지표 (RPM - Revolutions Per Minute)</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-spindle-speed',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'manufacturing-glossary',
      type: 'glossary',
      name: 'Manufacturing',
      fullyQualifiedName: 'Manufacturing',
      displayName: '제조 용어집',
      deleted: false,
    },
    synonyms: ['RPM', '회전수', 'Spindle RPM'],
    childrenCount: 0,
  },
  {
    id: 'term-feed-rate',
    name: 'FeedRate',
    displayName: '이송 속도',
    fullyQualifiedName: 'Manufacturing.FeedRate',
    description: '<p>공작 기계에서 절삭 공구가 공작물에 대해 이동하는 속도</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-feed-rate',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'manufacturing-glossary',
      type: 'glossary',
      name: 'Manufacturing',
      fullyQualifiedName: 'Manufacturing',
      displayName: '제조 용어집',
      deleted: false,
    },
    synonyms: ['Feed Speed', '이송률'],
    childrenCount: 0,
  },
  {
    id: 'term-tool-life',
    name: 'ToolLife',
    displayName: '공구 수명',
    fullyQualifiedName: 'Manufacturing.ToolLife',
    description: '<p>절삭 공구가 정상적인 절삭 성능을 유지할 수 있는 사용 시간 또는 절삭량</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-tool-life',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'manufacturing-glossary',
      type: 'glossary',
      name: 'Manufacturing',
      fullyQualifiedName: 'Manufacturing',
      displayName: '제조 용어집',
      deleted: false,
    },
    synonyms: ['Tool Wear', '공구 마모'],
    childrenCount: 0,
  },
  {
    id: 'term-cycle-time',
    name: 'CycleTime',
    displayName: '사이클 타임',
    fullyQualifiedName: 'Manufacturing.CycleTime',
    description: '<p>하나의 제품을 생산하는 데 소요되는 총 시간</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-cycle-time',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'manufacturing-glossary',
      type: 'glossary',
      name: 'Manufacturing',
      fullyQualifiedName: 'Manufacturing',
      displayName: '제조 용어집',
      deleted: false,
    },
    synonyms: ['생산 시간', 'Production Time'],
    childrenCount: 0,
  },
  {
    id: 'term-oee',
    name: 'OEE',
    displayName: 'OEE (Overall Equipment Effectiveness)',
    fullyQualifiedName: 'Manufacturing.OEE',
    description: '<p>설비 종합 효율. 가용률, 성능률, 양품률을 종합한 설비의 효율성 지표</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-oee',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'manufacturing-glossary',
      type: 'glossary',
      name: 'Manufacturing',
      fullyQualifiedName: 'Manufacturing',
      displayName: '제조 용어집',
      deleted: false,
    },
    synonyms: ['설비 종합 효율', 'Equipment Effectiveness'],
    childrenCount: 0,
  },
  {
    id: 'term-downtime',
    name: 'Downtime',
    displayName: '다운타임 (가동 중지 시간)',
    fullyQualifiedName: 'Manufacturing.Downtime',
    description: '<p>설비가 고장, 유지보수, 기타 이유로 가동되지 않는 시간</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-downtime',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'manufacturing-glossary',
      type: 'glossary',
      name: 'Manufacturing',
      fullyQualifiedName: 'Manufacturing',
      displayName: '제조 용어집',
      deleted: false,
    },
    synonyms: ['가동 중지', 'Equipment Downtime'],
    childrenCount: 0,
  },
  {
    id: 'term-vibration',
    name: 'Vibration',
    displayName: '진동',
    fullyQualifiedName: 'Manufacturing.Vibration',
    description: '<p>설비 가동 시 발생하는 물리적 떨림. 진동 수준은 설비 상태를 진단하는 중요한 지표입니다.</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-vibration',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'manufacturing-glossary',
      type: 'glossary',
      name: 'Manufacturing',
      fullyQualifiedName: 'Manufacturing',
      displayName: '제조 용어집',
      deleted: false,
    },
    synonyms: ['설비 진동', 'Equipment Vibration'],
    childrenCount: 0,
  },
  {
    id: 'term-temperature',
    name: 'Temperature',
    displayName: '온도',
    fullyQualifiedName: 'Manufacturing.Temperature',
    description: '<p>설비나 공정 중 측정되는 온도. 적절한 온도 관리는 품질과 설비 수명에 중요합니다.</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-temperature',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'manufacturing-glossary',
      type: 'glossary',
      name: 'Manufacturing',
      fullyQualifiedName: 'Manufacturing',
      displayName: '제조 용어집',
      deleted: false,
    },
    synonyms: ['설비 온도', 'Equipment Temperature'],
    childrenCount: 0,
  },
  {
    id: 'term-bearing',
    name: 'Bearing',
    displayName: '베어링',
    fullyQualifiedName: 'Manufacturing.Bearing',
    description: '<p>회전 운동을 지지하고 마찰을 줄이는 기계 부품. 베어링 상태는 설비 고장 예측의 핵심 요소입니다.</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-bearing',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'manufacturing-glossary',
      type: 'glossary',
      name: 'Manufacturing',
      fullyQualifiedName: 'Manufacturing',
      displayName: '제조 용어집',
      deleted: false,
    },
    synonyms: ['축수', 'Rolling Element Bearing'],
    childrenCount: 0,
  },
  {
    id: 'term-torque',
    name: 'Torque',
    displayName: '토크',
    fullyQualifiedName: 'Manufacturing.Torque',
    description: '<p>회전축에 가해지는 회전력. 토크 모니터링은 설비 상태와 부하를 파악하는 데 사용됩니다.</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-torque',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'manufacturing-glossary',
      type: 'glossary',
      name: 'Manufacturing',
      fullyQualifiedName: 'Manufacturing',
      displayName: '제조 용어집',
      deleted: false,
    },
    synonyms: ['회전력', 'Rotational Force'],
    childrenCount: 0,
  },
];

/**
 * 품질 관련 용어들
 */
const QUALITY_TERMS: GlossaryTerm[] = [
  {
    id: 'term-inspection',
    name: 'Inspection',
    displayName: '검사',
    fullyQualifiedName: 'Quality.Inspection',
    description: '<p>제품이나 부품이 규격과 요구사항을 충족하는지 확인하는 활동</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-inspection',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'quality-glossary',
      type: 'glossary',
      name: 'Quality',
      fullyQualifiedName: 'Quality',
      displayName: '품질 관리 용어집',
      deleted: false,
    },
    synonyms: ['Quality Inspection', '품질 검사'],
    childrenCount: 0,
  },
  {
    id: 'term-dimensional-inspection',
    name: 'DimensionalInspection',
    displayName: '치수 검사',
    fullyQualifiedName: 'Quality.DimensionalInspection',
    description: '<p>제품이나 부품의 크기, 형상, 위치 등의 치수가 도면이나 규격에 맞는지 측정하는 검사</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-dimensional-inspection',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'quality-glossary',
      type: 'glossary',
      name: 'Quality',
      fullyQualifiedName: 'Quality',
      displayName: '품질 관리 용어집',
      deleted: false,
    },
    synonyms: ['Dimension Measurement', '치수 측정'],
    childrenCount: 0,
  },
  {
    id: 'term-visual-inspection',
    name: 'VisualInspection',
    displayName: '외관 검사',
    fullyQualifiedName: 'Quality.VisualInspection',
    description: '<p>제품의 표면 상태, 색상, 결함 등을 육안이나 광학 장비로 확인하는 검사</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-visual-inspection',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'quality-glossary',
      type: 'glossary',
      name: 'Quality',
      fullyQualifiedName: 'Quality',
      displayName: '품질 관리 용어집',
      deleted: false,
    },
    synonyms: ['Appearance Inspection', '외관 검사'],
    childrenCount: 0,
  },
  {
    id: 'term-defect',
    name: 'Defect',
    displayName: '불량 (결함)',
    fullyQualifiedName: 'Quality.Defect',
    description:
      '<p>제품이나 부품이 요구사항을 충족하지 못하는 상태. 치수 불량, 외관 불량, 기능 불량 등이 있습니다.</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-defect',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'quality-glossary',
      type: 'glossary',
      name: 'Quality',
      fullyQualifiedName: 'Quality',
      displayName: '품질 관리 용어집',
      deleted: false,
    },
    synonyms: ['결함', 'Fault', 'Failure'],
    childrenCount: 0,
  },
  {
    id: 'term-defect-rate',
    name: 'DefectRate',
    displayName: '불량률',
    fullyQualifiedName: 'Quality.DefectRate',
    description: '<p>전체 생산량 중 불량품이 차지하는 비율. 품질 성과를 나타내는 주요 지표입니다.</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-defect-rate',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'quality-glossary',
      type: 'glossary',
      name: 'Quality',
      fullyQualifiedName: 'Quality',
      displayName: '품질 관리 용어집',
      deleted: false,
    },
    synonyms: ['Failure Rate', '결함률'],
    childrenCount: 0,
  },
  {
    id: 'term-tolerance',
    name: 'Tolerance',
    displayName: '공차',
    fullyQualifiedName: 'Quality.Tolerance',
    description: '<p>설계 치수에서 허용되는 변동 범위. 공차 내에 들어오면 합격으로 판정됩니다.</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-tolerance',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'quality-glossary',
      type: 'glossary',
      name: 'Quality',
      fullyQualifiedName: 'Quality',
      displayName: '품질 관리 용어집',
      deleted: false,
    },
    synonyms: ['허용 오차', 'Allowable Error'],
    childrenCount: 0,
  },
  {
    id: 'term-yield',
    name: 'Yield',
    displayName: '양품률',
    fullyQualifiedName: 'Quality.Yield',
    description: '<p>전체 생산량 중 양품(합격품)이 차지하는 비율. 불량률의 역지표입니다.</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-yield',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'quality-glossary',
      type: 'glossary',
      name: 'Quality',
      fullyQualifiedName: 'Quality',
      displayName: '품질 관리 용어집',
      deleted: false,
    },
    synonyms: ['Pass Rate', '합격률'],
    childrenCount: 0,
  },
  {
    id: 'term-spc',
    name: 'SPC',
    displayName: 'SPC (Statistical Process Control)',
    fullyQualifiedName: 'Quality.SPC',
    description: '<p>통계적 공정 관리. 공정의 변동을 통계적 방법으로 분석하여 품질을 관리하는 기법</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-spc',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'quality-glossary',
      type: 'glossary',
      name: 'Quality',
      fullyQualifiedName: 'Quality',
      displayName: '품질 관리 용어집',
      deleted: false,
    },
    synonyms: ['통계적 공정 관리', 'Process Control'],
    childrenCount: 0,
  },
  {
    id: 'term-cpk',
    name: 'Cpk',
    displayName: 'Cpk (Process Capability Index)',
    fullyQualifiedName: 'Quality.Cpk',
    description: '<p>공정 능력 지수. 공정의 산포와 규격 한계 대비 공정 평균의 위치를 고려한 공정 능력 지표</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-cpk',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'quality-glossary',
      type: 'glossary',
      name: 'Quality',
      fullyQualifiedName: 'Quality',
      displayName: '품질 관리 용어집',
      deleted: false,
    },
    synonyms: ['공정 능력 지수', 'Process Capability'],
    childrenCount: 0,
  },
  {
    id: 'term-durability-test',
    name: 'DurabilityTest',
    displayName: '내구 시험',
    fullyQualifiedName: 'Quality.DurabilityTest',
    description: '<p>제품이 장기간 사용 환경에서 견딜 수 있는 능력을 평가하는 시험</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-durability-test',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'quality-glossary',
      type: 'glossary',
      name: 'Quality',
      fullyQualifiedName: 'Quality',
      displayName: '품질 관리 용어집',
      deleted: false,
    },
    synonyms: ['Endurance Test', '내구성 시험'],
    childrenCount: 0,
  },
  {
    id: 'term-environmental-test',
    name: 'EnvironmentalTest',
    displayName: '환경 시험',
    fullyQualifiedName: 'Quality.EnvironmentalTest',
    description: '<p>제품이 다양한 환경 조건(온도, 습도, 진동 등)에서 정상적으로 작동하는지 확인하는 시험</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-environmental-test',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'quality-glossary',
      type: 'glossary',
      name: 'Quality',
      fullyQualifiedName: 'Quality',
      displayName: '품질 관리 용어집',
      deleted: false,
    },
    synonyms: ['Environmental Testing', '환경 조건 시험'],
    childrenCount: 0,
  },
  {
    id: 'term-functional-test',
    name: 'FunctionalTest',
    displayName: '기능 시험',
    fullyQualifiedName: 'Quality.FunctionalTest',
    description: '<p>제품이 설계된 기능을 정상적으로 수행하는지 확인하는 시험</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-functional-test',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'quality-glossary',
      type: 'glossary',
      name: 'Quality',
      fullyQualifiedName: 'Quality',
      displayName: '품질 관리 용어집',
      deleted: false,
    },
    synonyms: ['Function Test', '기능 테스트'],
    childrenCount: 0,
  },
];

/**
 * 데이터 관리 관련 용어들
 */
const DATA_MANAGEMENT_TERMS: GlossaryTerm[] = [
  {
    id: 'term-data-quality',
    name: 'DataQuality',
    displayName: '데이터 품질',
    fullyQualifiedName: 'DataManagement.DataQuality',
    description: '<p>데이터가 의도한 용도에 적합한 정도. 정확성, 완전성, 일관성, 적시성 등으로 평가됩니다.</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-data-quality',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'data-management-glossary',
      type: 'glossary',
      name: 'DataManagement',
      fullyQualifiedName: 'DataManagement',
      displayName: '데이터 관리 용어집',
      deleted: false,
    },
    synonyms: ['Data Quality Management', '데이터 품질 관리'],
    childrenCount: 0,
  },
  {
    id: 'term-metadata',
    name: 'Metadata',
    displayName: '메타데이터',
    fullyQualifiedName: 'DataManagement.Metadata',
    description: '<p>데이터에 대한 데이터. 데이터의 의미, 구조, 출처, 관계 등을 설명하는 정보</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-metadata',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'data-management-glossary',
      type: 'glossary',
      name: 'DataManagement',
      fullyQualifiedName: 'DataManagement',
      displayName: '데이터 관리 용어집',
      deleted: false,
    },
    synonyms: ['Data about Data', '데이터 정의'],
    childrenCount: 0,
    usageCount: 4,
  },
  {
    id: 'term-data-lineage',
    name: 'DataLineage',
    displayName: '데이터 계보',
    fullyQualifiedName: 'DataManagement.DataLineage',
    description:
      '<p>데이터가 생성, 이동, 변환되는 과정을 추적한 정보. 데이터의 출처와 흐름을 파악하는 데 사용됩니다.</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-data-lineage',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'data-management-glossary',
      type: 'glossary',
      name: 'DataManagement',
      fullyQualifiedName: 'DataManagement',
      displayName: '데이터 관리 용어집',
      deleted: false,
    },
    synonyms: ['Data Provenance', '데이터 계통'],
    childrenCount: 0,
  },
  {
    id: 'term-data-governance',
    name: 'DataGovernance',
    displayName: '데이터 거버넌스',
    fullyQualifiedName: 'DataManagement.DataGovernance',
    description: '<p>조직의 데이터 자산을 효과적으로 관리하고 활용하기 위한 정책, 프로세스, 역할 체계</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-data-governance',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'data-management-glossary',
      type: 'glossary',
      name: 'DataManagement',
      fullyQualifiedName: 'DataManagement',
      displayName: '데이터 관리 용어집',
      deleted: false,
    },
    synonyms: ['Data Management Framework', '데이터 관리 체계'],
    childrenCount: 0,
  },
  {
    id: 'term-master-data',
    name: 'MasterData',
    displayName: '마스터 데이터',
    fullyQualifiedName: 'DataManagement.MasterData',
    description: '<p>조직의 핵심 비즈니스 엔티티를 정의하는 데이터. 고객, 제품, 설비, 위치 등의 참조 정보</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-master-data',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'data-management-glossary',
      type: 'glossary',
      name: 'DataManagement',
      fullyQualifiedName: 'DataManagement',
      displayName: '데이터 관리 용어집',
      deleted: false,
    },
    synonyms: ['Reference Data', '참조 데이터'],
    childrenCount: 0,
  },
  {
    id: 'term-data-catalog',
    name: 'DataCatalog',
    displayName: '데이터 카탈로그',
    fullyQualifiedName: 'DataManagement.DataCatalog',
    description: '<p>조직 내 모든 데이터 자산을 검색, 이해, 활용할 수 있도록 정리한 목록</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-data-catalog',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'data-management-glossary',
      type: 'glossary',
      name: 'DataManagement',
      fullyQualifiedName: 'DataManagement',
      displayName: '데이터 관리 용어집',
      deleted: false,
    },
    synonyms: ['Data Inventory', '데이터 목록'],
    childrenCount: 0,
  },
  {
    id: 'term-data-lake',
    name: 'DataLake',
    displayName: '데이터 레이크',
    fullyQualifiedName: 'DataManagement.DataLake',
    description: '<p>다양한 형태의 원시 데이터를 중앙 저장소에 대량으로 저장하는 아키텍처</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-data-lake',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'data-management-glossary',
      type: 'glossary',
      name: 'DataManagement',
      fullyQualifiedName: 'DataManagement',
      displayName: '데이터 관리 용어집',
      deleted: false,
    },
    synonyms: ['Raw Data Repository', '원시 데이터 저장소'],
    childrenCount: 0,
  },
  {
    id: 'term-data-warehouse',
    name: 'DataWarehouse',
    displayName: '데이터 웨어하우스',
    fullyQualifiedName: 'DataManagement.DataWarehouse',
    description: '<p>의사결정 지원을 위해 구조화되고 통합된 데이터를 저장하는 시스템</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-data-warehouse',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'data-management-glossary',
      type: 'glossary',
      name: 'DataManagement',
      fullyQualifiedName: 'DataManagement',
      displayName: '데이터 관리 용어집',
      deleted: false,
    },
    synonyms: ['DW', 'Enterprise Data Warehouse'],
    childrenCount: 0,
  },
  {
    id: 'term-feature-engineering',
    name: 'FeatureEngineering',
    displayName: '피처 엔지니어링',
    fullyQualifiedName: 'DataManagement.FeatureEngineering',
    description:
      '<p>머신러닝 모델의 성능을 향상시키기 위해 원시 데이터로부터 특징(feature)을 생성하고 선택하는 과정</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-feature-engineering',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'data-management-glossary',
      type: 'glossary',
      name: 'DataManagement',
      fullyQualifiedName: 'DataManagement',
      displayName: '데이터 관리 용어집',
      deleted: false,
    },
    synonyms: ['Feature Extraction', '특징 추출'],
    childrenCount: 0,
  },
  {
    id: 'term-machine-learning',
    name: 'MachineLearning',
    displayName: '머신러닝',
    fullyQualifiedName: 'DataManagement.MachineLearning',
    description: '<p>데이터로부터 패턴을 학습하고 예측하는 인공지능 기술</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-machine-learning',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'data-management-glossary',
      type: 'glossary',
      name: 'DataManagement',
      fullyQualifiedName: 'DataManagement',
      displayName: '데이터 관리 용어집',
      deleted: false,
    },
    synonyms: ['ML', '기계학습'],
    childrenCount: 0,
  },
  {
    id: 'term-preprocessing',
    name: 'Preprocessing',
    displayName: '전처리',
    fullyQualifiedName: 'DataManagement.Preprocessing',
    description:
      '<p>데이터를 분석이나 모델 학습에 적합한 형태로 변환하는 과정. 결측치 처리, 정규화, 인코딩 등을 포함합니다.</p>',
    version: 0.1,
    updatedAt: 1761833744665,
    updatedBy: 'admin',
    href: 'http://localhost:8585/v1/glossaryTerms/term-preprocessing',
    deleted: false,
    status: 'Approved',
    provider: 'user',
    mutuallyExclusive: false,
    glossary: {
      id: 'data-management-glossary',
      type: 'glossary',
      name: 'DataManagement',
      fullyQualifiedName: 'DataManagement',
      displayName: '데이터 관리 용어집',
      deleted: false,
    },
    synonyms: ['Data Preprocessing', '데이터 전처리'],
    childrenCount: 0,
  },
];

/**
 * 모든 용어
 */
export const ALL_GLOSSARY_TERMS = [...MANUFACTURING_TERMS, ...QUALITY_TERMS, ...DATA_MANAGEMENT_TERMS];

/**
 * 용어집 카테고리 목록 조회
 */
export const getGlossaryCategoriesMockData = (limit: number = 1000) => {
  return {
    data: GLOSSARY_CATEGORIES.slice(0, limit),
    paging: {
      total: GLOSSARY_CATEGORIES.length,
    },
  };
};

/**
 * 특정 용어집의 용어 목록 조회 (FQN으로)
 */
export const getGlossaryTermsMockData = (glossaryFqn: string, limit: number = 1000) => {
  const filteredTerms = ALL_GLOSSARY_TERMS.filter((term) => term.fullyQualifiedName.startsWith(`${glossaryFqn}.`));

  // 필수 필드 보완
  const enrichedTerms = filteredTerms.map((term) => ({
    ...term,
    status: term.status || 'Approved',
    provider: term.provider || 'user',
    mutuallyExclusive: term.mutuallyExclusive || false,
    deleted: term.deleted || false,
    synonyms: term.synonyms || [],
    relatedTerms: term.relatedTerms || [],
    owners: term.owners || [],
  }));

  return {
    data: enrichedTerms.slice(0, limit),
    paging: {
      total: enrichedTerms.length,
    },
  };
};

/**
 * 특정 용어 상세 정보 조회 (FQN으로)
 */
export const getGlossaryTermByFqn = (fqn: string): GlossaryTerm | null => {
  const term = ALL_GLOSSARY_TERMS.find((term) => term.fullyQualifiedName === fqn);

  if (!term) return null;

  // 필수 필드 보완
  return {
    ...term,
    status: term.status || 'Approved',
    provider: term.provider || 'user',
    mutuallyExclusive: term.mutuallyExclusive || false,
    deleted: term.deleted || false,
    synonyms: term.synonyms || [],
    relatedTerms: term.relatedTerms || [],
    tags: term.tags || [],
    owners: term.owners || [],
    domains: term.domains || [],
    reviewers: term.reviewers || [],
    references: term.references || [],
    childrenCount: term.childrenCount || 0,
    glossary: term.glossary || {
      id: term.fullyQualifiedName.split('.')[0] + '-glossary',
      type: 'glossary',
      name: term.fullyQualifiedName.split('.')[0],
      fullyQualifiedName: term.fullyQualifiedName.split('.')[0],
      displayName: term.fullyQualifiedName.split('.')[0],
      deleted: false,
    },
  };
};

/**
 * 특정 용어집 카테고리 상세 정보 조회 (FQN으로)
 */
export const getGlossaryCategoryByFqn = (fqn: string): GlossaryCategory | null => {
  return GLOSSARY_CATEGORIES.find((category) => category.fullyQualifiedName === fqn) || null;
};

/**
 * 특정 부모의 직계 자식 용어들만 조회 (트리 구조용)
 */
export const getDirectChildrenTerms = (parentFqn: string, limit: number = 3000) => {
  // 모든 용어 중에서 부모가 일치하는 용어만 필터링
  const childTerms = ALL_GLOSSARY_TERMS.filter((term) => {
    // parentFqn이 용어집 이름인 경우 (Manufacturing, Quality, DataManagement)
    const isGlossaryLevel = GLOSSARY_CATEGORIES.some((cat) => cat.fullyQualifiedName === parentFqn);

    if (isGlossaryLevel) {
      // 용어집 바로 아래 1단계 용어들만 (점이 하나만 있는 용어)
      return term.fullyQualifiedName.startsWith(`${parentFqn}.`) && term.fullyQualifiedName.split('.').length === 2;
    } else {
      // 특정 용어의 직계 자식들
      return term.parent?.fullyQualifiedName === parentFqn;
    }
  });

  // 필수 필드 보완
  const enrichedTerms = childTerms.map((term) => ({
    ...term,
    status: term.status || 'Approved',
    provider: term.provider || 'user',
    mutuallyExclusive: term.mutuallyExclusive || false,
    deleted: term.deleted || false,
    synonyms: term.synonyms || [],
    relatedTerms: term.relatedTerms || [],
    owners: term.owners || [],
  }));

  return {
    data: enrichedTerms.slice(0, limit),
    paging: {
      total: enrichedTerms.length,
    },
  };
};
